import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lightning, ArrowRight, Gift, GoogleLogo, ShieldCheck } from "@phosphor-icons/react";
import client, { saveAuth } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

const Auth = ({ mode }) => {
  const isLogin = mode === "login";
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const refCode = sp.get("ref") || "";
  const [form, setForm] = useState({ email: "", password: "", name: "", slug: "", ref: refCode, code: "" });
  const [loading, setLoading] = useState(false);
  const [needs2fa, setNeeds2fa] = useState(false);

  useEffect(() => {
    if (refCode) setForm(f => ({ ...f, ref: refCode }));
  }, [refCode]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin
        ? { email: form.email, password: form.password, code: form.code || undefined }
        : form;
      const { data } = await client.post(url, payload);
      saveAuth(data.access_token, data.user);
      toast.success(isLogin ? "Welcome back." : "Account created.");
      nav("/dashboard");
    } catch (err) {
      if (err?.response?.status === 428) {
        setNeeds2fa(true);
        toast.message("Enter the 6-digit code from your authenticator app");
      } else {
        toast.error(err?.response?.data?.detail || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Preserve referral code across the OAuth round-trip via sessionStorage
    if (refCode) sessionStorage.setItem("k_pending_ref", refCode);
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0A0D14] text-white flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 border-r border-white/10 p-12 relative k-grid">
        <Link to="/" className="flex items-center gap-2" data-testid="auth-logo">
          <BrandLogo height={60} />
        </Link>
        <div>
          <h2 className="font-display text-6xl tracking-tighter leading-none">
            ONE LINK. <br /><span className="text-[#FACC15]">EVERY</span> <br /> REVENUE STREAM.
          </h2>
          <p className="mt-6 text-[#A1A1AA] font-mono-k max-w-md">
            Bio pages, shops, funnels, campaigns, AI copilot, wallet — all in one operator-grade toolkit.
          </p>
        </div>
        <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">© 2026 KORPORATE</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-3">— {isLogin ? "LOG IN" : "CREATE ACCOUNT"}</div>
          <h1 className="font-display text-4xl tracking-tighter mb-6">{isLogin ? "Welcome back." : "Get your page live in 60 seconds."}</h1>

          {refCode && !isLogin && (
            <div className="border border-[#FACC15] bg-[#FACC15]/10 p-4 mb-6 flex items-center gap-3" data-testid="referred-banner">
              <Gift size={22} weight="duotone" className="text-[#FACC15]" />
              <div className="text-sm font-mono-k">You were referred by <span className="text-[#FACC15]">{refCode}</span>. They'll earn a commission if you upgrade to Pro.</div>
            </div>
          )}

          <button type="button" onClick={googleSignIn} className="k-btn w-full justify-center bg-white text-black border-white hover:bg-white hover:text-black hover:!bg-[#f5f5f5]" data-testid="google-signin">
            <GoogleLogo weight="bold" size={18} /> CONTINUE WITH GOOGLE
          </button>
          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 border-t border-white/10"></div>
            <span className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA]">OR</span>
            <div className="flex-1 border-t border-white/10"></div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">FULL NAME</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="k-input mt-1" required data-testid="auth-name" />
                </div>
                <div>
                  <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">USERNAME / SLUG (korporatelinks.com/u/…)</label>
                  <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="k-input mt-1" placeholder="yourhandle" data-testid="auth-slug" />
                </div>
              </>
            )}
            <div>
              <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">EMAIL</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="k-input mt-1" required data-testid="auth-email" />
            </div>
            <div>
              <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">PASSWORD</label>
              <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="k-input mt-1" required minLength={6} data-testid="auth-password" />
              {isLogin && (
                <Link to="/forgot-password" className="text-xs font-mono-k text-[#FACC15] hover:underline mt-2 inline-block" data-testid="auth-forgot">Forgot password?</Link>
              )}
            </div>
            {needs2fa && (
              <div className="border border-[#FACC15] bg-[#FACC15]/10 p-3" data-testid="tfa-block">
                <div className="flex items-center gap-2 mb-2"><ShieldCheck size={16} className="text-[#FACC15]" /> <span className="text-xs font-mono-k tracking-widest-k text-[#FACC15]">TWO-FACTOR AUTHENTICATION</span></div>
                <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">6-DIGIT CODE</label>
                <input inputMode="numeric" pattern="[0-9]*" maxLength={6} autoFocus value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="k-input mt-1 font-mono-k text-lg tracking-widest text-center" placeholder="123456" data-testid="auth-2fa-code" required />
              </div>
            )}
            <button type="submit" disabled={loading} className="k-btn primary w-full justify-center mt-6" data-testid="auth-submit">
              {loading ? "…" : (isLogin ? "LOG IN" : "CREATE ACCOUNT")} <ArrowRight size={16} />
            </button>
          </form>

          <div className="mt-6 text-sm font-mono-k text-[#A1A1AA]">
            {isLogin ? (
              <>No account? <Link to="/register" className="text-[#FACC15] hover:underline" data-testid="auth-switch">Create one →</Link></>
            ) : (
              <>Already have an account? <Link to="/login" className="text-[#FACC15] hover:underline" data-testid="auth-switch">Log in →</Link></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
